import React, { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import { useLocation, useNavigate } from "react-router-dom"
import Sidebar, { toTitle } from "../Sidebar/Sidebar"
import SidebarBrand from "../Sidebar/SidebarBrand"
import Header from "../Header/Header"
import DataSourceBadge from "../../layout/DataSourceBadge"
import NotificationsBell from "../../layout/NotificationsBell"
import UserMenu from "../../layout/UserMenu"
import { DownloadCenterProvider } from "../../../pages/Notifications/downloadCenter"
import { getUserAccess, getUserData } from "../../../utils/authV2/tokenManager"
import { getRouteContext } from "../../../utils/routeContext"
import { toRouteSlug } from "../../../utils/routeSlug"
import logo from "../../../assets/images/logo/Group.svg"
import s from "./AppShell.module.scss"

/**
 * Breadcrumb for the routes that aren't project-scoped.
 *
 * `getRouteContext` returns nothing for these, so without a label here they
 * all fall back to "Dashboard" — which names a screen the reader isn't on.
 */
const STATIC_CRUMBS = {
  "/notifications": "Notifications",
  "/all-project": "All Projects",
  "/user-access-panel": "User Access Panel",
  "/project-access-panel": "Project Access Panel"
}

/**
 * Which access panels a user may open.
 *
 * `is_superuser` is gone from `user_detail`. Staff SSO sends two flags in
 * its place, and they are not the same permission:
 *
 *   is_sso_admin  the super admin — every app, project, role and user, and
 *                 the only one who may edit the catalog of projects and
 *                 screens. Both panels.
 *   can_manage    a manager — creates and edits roles and users, but only
 *                 within what their own role already holds. The user panel
 *                 alone: the catalog is not theirs to change.
 *
 * Neither flag, no entry at all. A super admin who is also a manager sees
 * both, which is why this is two independent flags and not one level.
 *
 * @param userData the decoded `user_detail`
 * @returns the panels to list, in order; empty for everyone else
 */
export const accessPanels = (userData) => {
  const isSsoAdmin = Boolean(userData?.is_sso_admin)
  const canManage = Boolean(userData?.can_manage)

  if (!isSsoAdmin && !canManage) return []

  return [
    { key: "user-access", label: "User Access Panel", path: "/user-access-panel" },
    ...(isSsoAdmin
      ? [{ key: "project-access", label: "Project Access Panel", path: "/project-access-panel" }]
      : [])
  ]
}

/**
 * Application chrome: sidebar + header + content well.
 *
 * This is the one place that knows about auth (`getUserData`) and routing, so
 * Sidebar and Header can stay presentational. Route shape is resolved through
 * `getRouteContext`, which already handles both `/:project/:module` and the
 * legacy `/:category/:project/:module` forms.
 */
const AppShell = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  // Two independent states because the same button means two things: below lg
  // the sidebar is an off-canvas drawer that opens over the page; at lg and up
  // it's a permanent panel that collapses to an icon rail.
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const { project, module, vertical } = getRouteContext(location.pathname)

  // `getUserData()` parses localStorage on every call, so memoise it — otherwise
  // the derived lists below get a new identity each render.
  const userData = useMemo(() => getUserData() || {}, [])

  const projects = useMemo(() => {
    const access = Array.isArray(userData.access) ? userData.access : getUserAccess() || []
    return access.map((entry) => ({
      project: entry.project,
      label: toTitle(entry.project),
      modules: Array.isArray(entry.modules) ? entry.modules : []
    }))
  }, [userData])

  // Preserve the legacy vertical prefix when the current route uses it,
  // otherwise emit the short two-segment form.
  const buildPath = useCallback(
    (nextProject, nextModule) => {
      const prefix = vertical ? `/${vertical}` : ""
      return nextModule ? `${prefix}/${nextProject}/${nextModule}` : `${prefix}/${nextProject}`
    },
    [vertical]
  )

  /**
   * Header menu button. Previously this only ever set `sidebarOpen`, which is
   * scoped to a `max-width` media query — so on a desktop viewport clicking it
   * did nothing at all.
   */
  const toggleSidebar = useCallback(() => {
    // Mirrors $av-bp-lg in styles/tokens/_mixins.scss.
    if (window.matchMedia("(min-width: 1024px)").matches) {
      setSidebarCollapsed((collapsed) => !collapsed)
    } else {
      setSidebarOpen((open) => !open)
    }
  }, [])

  const handleNavigate = useCallback(
    ({ project: nextProject, module: nextModule }) => {
      if (!nextProject) return
      navigate(buildPath(nextProject, nextModule))
      setSidebarOpen(false)
    },
    [buildPath, navigate]
  )

  const breadcrumbs = useMemo(() => {
    const crumbs = []
    if (project) {
      // The URL segment is a slug, so title-casing it alone yields
      // "Productiontoolkit". Prefer the granted name the sidebar displays,
      // matching on slug; fall back for projects not in the access list.
      const slug = toRouteSlug(project)
      const granted = projects.find((p) => toRouteSlug(p.project) === slug)
      crumbs.push({
        label: granted?.label || toTitle(project),
        onClick: () => navigate(buildPath(project))
      })
    }
    if (module) crumbs.push({ label: module.toUpperCase() })
    if (crumbs.length) return crumbs

    const staticCrumb = STATIC_CRUMBS[location.pathname.replace(/\/+$/, "")]
    return [{ label: (staticCrumb ?? "Dashboard").toUpperCase() }]
  }, [project, module, projects, buildPath, navigate, location.pathname])

  const footerItems = useMemo(() => {
    // `accessPanels` reads the flags; this only turns them into nav rows.
    // One definition of each destination: the sidebar navigates by `onClick`
    // and highlights by `path`, and writing the route twice is how those two
    // drift apart.
    const entry = (key, label, path) => ({ key, label, path, onClick: () => navigate(path) })

    const items = []
    const panels = accessPanels(userData)

    if (panels.length) {
      items.push({
        key: "access-panel",
        label: "My Access Panel",
        icon: "shield",
        children: panels.map((panel) => entry(panel.key, panel.label, panel.path))
      })
    }
    items.push({ key: "settings", label: "Settings", icon: "settings", onClick: () => {} })
    return items
  }, [userData, navigate])

  return (
    /*
      The download centre wraps the whole shell, not just the bell: an Export
      pressed on any table inside `children` has to be able to raise the
      notification panel in the header. The queue it reads is scoped to one
      project by the request headers, so the cards are labelled with the
      project whose screen is open.
    */
    <DownloadCenterProvider project={project ? toTitle(project) : undefined}>
      <div className={s.shell}>
        <div className={cx(s.sidebarSlot, { [s.sidebarOpen]: sidebarOpen })}>
          <Sidebar
            projects={projects}
            activeProject={project}
            activeModule={module}
            activePath={location.pathname}
            collapsed={sidebarCollapsed}
            onRequestExpand={() => setSidebarCollapsed(false)}
            onNavigate={handleNavigate}
            footerItems={footerItems}
            brand={<SidebarBrand logoSrc={logo} onClick={() => navigate("/all-project")} />}
          />
        </div>

        {sidebarOpen && (
          <button
            type="button"
            className={s.overlay}
            aria-label="Close navigation"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className={s.main}>
          <Header
            breadcrumbs={breadcrumbs}
            /* Says whether this screen's numbers are real — see `dataSources`. */
            actions={<DataSourceBadge />}
            notificationSlot={<NotificationsBell />}
            userSlot={<UserMenu />}
            onToggleSidebar={toggleSidebar}
          />
          <main className={s.content}>
            <div className={s.stack}>{children}</div>
          </main>
        </div>
      </div>
    </DownloadCenterProvider>
  )
}

AppShell.propTypes = {
  children: PropTypes.node
}

export default AppShell
